import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { IPaginated, ITableSort, localStorageEnum } from 'types';
import { geTableSortQueryParam } from 'utils/helpers';
import { API_URL } from '../utils/constants';
import { ICompanyGroup } from './containers/Company/slice/types';
import { ICulture } from './containers/Cultures/slice/types';
import {
  ISeasonForFieldImport,
  IUploadPolygonData,
} from './containers/Field/slice/types';
import {
  IAccountingMethod,
  IAddTransportPayload,
  IGpsTransport,
  ITransport,
  ITransportFilters,
  ITransportResponse,
  IGarageUnitType,
  IUpdateTransportPayload,
  IGpsImplement,
  IAddImplementPayload,
  IUpdateImplementPayload,
  IImplementResponse,
  IImplement,
  IAddEmployeePayload,
  IUpdateEmployeePayload,
  IEmployeeResponse,
  IEmployee,
  IEmployeeFilters,
  IGpsEmployee,
} from './containers/Garage/types';
import { handleError } from './containers/Global/slice/helpers';
import { reorder } from './containers/Map/slice/helpers';
import {
  IFieldsResponse,
  ITransportPositionResponse,
} from './containers/Map/slice/types';
import {
  ICreateRecipePayload,
  IRecipe,
  IRecipeCategory,
  IRecipeFilters,
  IRecipeResponse,
  IUpdateRecipePayload,
} from './containers/Recipe/types';
import {
  IResourceType,
  IUsedResourceByResourceType,
  IUsedResourcePriceCreateViewItem,
  IUsedResourcePricesResponse,
} from './containers/Resource/slice/types';
import {
  ITaskCategoryPrice,
  IUpdateTaskCategoryPricePayload,
} from './containers/Settings/types';
import {
  ICreateTask,
  ICreateTaskRecommendationBody,
  IEditTaskRecommendationBody,
  IRoutesWithoutTaskFilters,
  ITask,
  ITaskAvailableResource,
  ITaskCategory,
  ITaskListResponse,
  RouteData,
} from './containers/Task/slice/types';
import {
  IHikosEnvPermissionsResponse,
  IUserListResponseItem,
} from './containers/User/slice/types';

const baseQuery = fetchBaseQuery({
  baseUrl: API_URL,
  prepareHeaders: headers => {
    const token = localStorage.getItem(localStorageEnum.token) || '';
    if (token) {
      headers.set('Authorization', token);
    }
    return headers;
  },
});

const baseQueryWithAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  if (result.error?.status === 500 || result.error?.status === 403) {
    handleError(result.error);
  }

  return result;
};

export const api = createApi({
  refetchOnMountOrArgChange: true,
  reducerPath: 'hikosApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: [
    'permissions',
    'company',
    'companyGroup',

    'privacyPolicy',
    'usedResourcePrice',
    'taskCategoryPrice',
    'taskCategory',
    'transport',
    'task',
    'implement',
    'employee',
    'recipe',
    'route',
  ],
  endpoints: builder => ({
    // Polygons
    uploadPolygon: builder.mutation<
      unknown,
      { envId: number; data: IUploadPolygonData }
    >({
      query: ({ envId, data }) => ({
        method: 'POST',
        url: `hiiko_environment/${envId}/fields/upload-polygon`,
        body: data,
      }),
      invalidatesTags: [],
    }),

    updatePolygonGeometry: builder.mutation<
      unknown,
      { envId: number; fieldId: number; data: IUploadPolygonData }
    >({
      query: ({ fieldId, envId, data }) => ({
        method: 'POST',
        url: `hiiko_environment/${envId}/fields/update-field-geometry/${fieldId}`,
        body: data,
      }),
      invalidatesTags: [],
    }),

    // Transport on map
    getTransportPosition: builder.query<
      ITransportPositionResponse,
      { seasonId: number; envId: number }
    >({
      query: ({ seasonId, envId }) => ({
        url: `geoserver/wfs?tex_k_p=d&service=WFS&version=1.1.0&request=GetFeature&typename=gps_workspace%3Agps_live_view&srsName=EPSG%3A4326&outputFormat=application%2Fjson`,
        params: {
          request_type: 'LIVE',
          hiiko_environment_id: envId,
          season_id: seasonId,
        },
      }),
    }),

    // Fields on map
    getFieldsGeometry: builder.query<
      IFieldsResponse,
      {
        envId: number;
        seasonId: number;
        companyId: number | null;
        companyGroupId: number | null;
      }
    >({
      query: ({ envId, seasonId, companyId, companyGroupId }) => ({
        url: `geoserver/wfs`,
        params: {
          hiiko_environment_id: envId,
          season_id: seasonId,
          service: 'WFS',
          version: '1.1.0',
          request: 'GetFeature',
          typename: 'nyc:field_season_view', // nyc:nyc_buildings_area_view
          srsName: 'EPSG:4326', // world
          outputFormat: 'application/json',

          company_id: companyId || undefined,
          company_group_id: companyGroupId || undefined,
        },
      }),
      transformResponse: (response: IFieldsResponse): IFieldsResponse => {
        return {
          ...response,
          features: response.features.map(feature => ({
            ...feature,
            geometry: {
              ...feature.geometry,
              coordinates: reorder([[feature.geometry.coordinates as any]]),
            },
          })),
        };
      },
    }),

    // Routes on map

    getRoutes: builder.query<
      IPaginated<RouteData>,
      {
        envId: number;
        filters: IRoutesWithoutTaskFilters;
        routesInFieldsOnly: boolean;
      }
    >({
      query: ({ envId, filters, routesInFieldsOnly }) => {
        const culture_id = filters.cultureIds?.length
          ? filters.cultureIds
          : undefined;
        const vehicle_id = filters.gpsVehicleIds?.length
          ? filters.gpsVehicleIds
          : undefined;

        const from = filters.startDate;
        const to = filters.endDate;

        return {
          url: `/hiiko_environment/${envId}/gps_route/`,
          params: {
            culture_id,
            vehicle_id,
            from,
            to,
            size: 100,
            sort: 'from,DESC',
            routes_in_fields: routesInFieldsOnly,
          },
        };
      },
      transformResponse: (routesResponse: IPaginated<RouteData>) => {
        return {
          ...routesResponse,
          content: routesResponse.content.map(route => {
            return {
              ...route,
              geometry: {
                ...route.geometry,
                coordinates: reorder([[route.geometry.coordinates as any]]),
              },
            };
          }),
        };
      },
      providesTags: ['route'],
    }),

    removeRoute: builder.mutation<unknown, { envId: number; routeId: number }>({
      query: ({ envId, routeId }) => ({
        method: 'DELETE',
        url: `hiiko_environment/${envId}/gps_route/${routeId}`,
      }),
      invalidatesTags: ['route'],
    }),

    assignBeacon: builder.mutation<
      unknown,
      { envId: number; routeId: number; beaconId: string }
    >({
      query: ({ envId, routeId, beaconId }) => ({
        method: 'PUT',
        url: `hiiko_environment/${envId}/gps_route/${routeId}/beacon/${beaconId}`,
      }),
      invalidatesTags: ['route'],
    }),

    assignSlim: builder.mutation<
      unknown,
      { envId: number; routeId: number; slimId: string }
    >({
      query: ({ envId, routeId, slimId }) => ({
        method: 'PUT',
        url: `hiiko_environment/${envId}/gps_route/${routeId}/slim/${slimId}`,
      }),
      invalidatesTags: ['route'],
    }),

    // Company
    acceptSharedCompany: builder.mutation<
      IHikosEnvPermissionsResponse,
      { envId: number; companyId: number }
    >({
      query: ({ envId, companyId }) => ({
        method: 'POST',
        url: `hiiko_environment/${envId}/company/${companyId}/accept`,
      }),
      invalidatesTags: ['permissions', 'company'],
    }),

    removeSharedCompany: builder.mutation<
      IHikosEnvPermissionsResponse,
      { envId: number; companyId: number; guestEnvId: number }
    >({
      query: ({ envId, companyId, guestEnvId }) => ({
        method: 'DELETE',
        url: `hiiko_environment/${envId}/company/${companyId}/remove`,
        params: { remove_from_hiiko_environment_id: guestEnvId },
      }),
      invalidatesTags: ['permissions', 'company'],
    }),

    // Company Group
    createCompanyGroup: builder.mutation<
      ICompanyGroup,
      { envId: number; name: string; companyIds: number[] }
    >({
      query: ({ envId, name, companyIds }) => ({
        method: 'POST',
        url: `/hiiko_environment/${envId}/company-group`,
        body: { name, companyIds },
      }),
      invalidatesTags: ['companyGroup'],
    }),

    updateCompanyGroup: builder.mutation<
      ICompanyGroup,
      {
        envId: number;
        name: string;
        companyIds: number[];
        companyGroupId: number;
      }
    >({
      query: ({ envId, name, companyIds, companyGroupId }) => ({
        method: 'PUT',
        url: `/hiiko_environment/${envId}/company-group/${companyGroupId}`,
        body: { name, companyIds },
      }),
      invalidatesTags: ['companyGroup'],
    }),

    archiveCompanyGroup: builder.mutation<
      unknown,
      {
        envId: number;
        companyGroupId: number;
      }
    >({
      query: ({ envId, companyGroupId }) => ({
        method: 'PATCH',
        url: `/hiiko_environment/${envId}/company-group/${companyGroupId}/archive`,
      }),
      invalidatesTags: ['companyGroup'],
    }),

    getAllCompanyGroups: builder.query<ICompanyGroup[], { envId: number }>({
      query: ({ envId }) => ({
        url: `/hiiko_environment/${envId}/company-group`,
      }),
      transformResponse: (response: ICompanyGroup[]) =>
        response?.sort((a, b) => a.name.localeCompare(b.name)),
      providesTags: ['companyGroup'],
    }),

    // Permissions
    getPermissions: builder.query<
      IHikosEnvPermissionsResponse,
      { envId: number }
    >({
      query: ({ envId }) => ({
        url: `user/hiiko-environment-permissions`,
        params: {
          hiiko_environment_id: envId,
        },
      }),
      providesTags: ['permissions'],
    }),

    // User
    getAllUsers: builder.query<IUserListResponseItem[], { envId: number }>({
      query: ({ envId }) => `hiiko_environment/${envId}/users`,
    }),

    getPrivacyPolicy: builder.query<boolean, void>({
      query: () => ({
        url: `/user/privacy_policy`,
      }),
      providesTags: ['privacyPolicy'],
    }),

    acceptPrivacyPolicy: builder.mutation<boolean, void>({
      query: () => ({
        method: 'POST',
        url: `/user/privacy_policy`,
      }),
      invalidatesTags: ['privacyPolicy'],
    }),

    getSeasonsForFieldImport: builder.query<
      ISeasonForFieldImport[],
      {
        envId: number;
        companyId: number;
      }
    >({
      query: ({ envId, companyId }) => ({
        url: `hiiko_environment/${envId}/seasons/import-seasons`,
        params: { company_id: companyId },
      }),
    }),

    // Resource
    getAllResourceTypes: builder.query<IResourceType[], void>({
      query: () => ({
        url: 'resource-type',
      }),
    }),

    getAllResources: builder.query<ITaskAvailableResource[], { envId: number }>(
      {
        query: ({ envId }) => ({
          url: `/hiiko_environment/${envId}/resources/all`,
        }),
      },
    ),

    // Used Resource
    getUsedResourcePrices: builder.query<
      IUsedResourcePricesResponse,
      {
        envId: number;
        seasonId: number;
        resourceId: number;
      }
    >({
      query: ({ envId, seasonId, resourceId }) => ({
        url: `hiiko_environment/${envId}/used-resources/prices/${resourceId}`,
        params: {
          season_id: seasonId,
        },
      }),
      providesTags: ['usedResourcePrice'],
    }),

    addUsedResourcePrices: builder.mutation<
      IUsedResourcePricesResponse,
      {
        envId: number;
        usedResourcePriceCreateViewList: IUsedResourcePriceCreateViewItem[];
      }
    >({
      query: ({ envId, usedResourcePriceCreateViewList }) => ({
        method: 'POST',
        url: `hiiko_environment/${envId}/used-resources/prices`,
        body: usedResourcePriceCreateViewList,
      }),
      invalidatesTags: ['usedResourcePrice'],
    }),

    getUsedResourcesByResourceType: builder.query<
      IUsedResourceByResourceType[],
      {
        envId: number;
        seasonId: number;
        companyId?: number;
        resourceTypeId?: number;
        textSearch?: string;
      }
    >({
      query: ({ envId, companyId, seasonId, resourceTypeId, textSearch }) => ({
        url: `hiiko_environment/${envId}/used-resources`,
        params: {
          company_id: companyId,
          season_id: seasonId,
          resource_type_id: resourceTypeId,
          name: textSearch || undefined,
        },
      }),
      providesTags: ['usedResourcePrice'],
    }),

    getTaskCategoryPrices: builder.query<
      ITaskCategoryPrice[],
      {
        envId: number;
        seasonId: number;
        companyId: number | null;
        taskCategoryId?: number;
      }
    >({
      query: ({ envId, companyId, seasonId, taskCategoryId }) => ({
        url: `hiiko_environment/${envId}/task_category/prices`,
        params: {
          company_id: companyId ?? undefined,
          season_id: seasonId,
          task_category_id: taskCategoryId,
        },
      }),
      providesTags: ['taskCategoryPrice'],
    }),
    updateTaskCategoryPrice: builder.mutation<
      unknown,
      IUpdateTaskCategoryPricePayload
    >({
      query: payload => ({
        method: 'POST',
        url: `hiiko_environment/${payload.hiikoEnvironmentId}/task_category/prices`,
        body: payload,
      }),
      invalidatesTags: ['taskCategoryPrice'],
    }),

    getTaskCategories: builder.query<ITaskCategory[], { envId: number }>({
      transformResponse: (response: ITaskCategory[]) =>
        response?.sort((a, b) => a.name.localeCompare(b.name)),

      query: ({ envId }) => ({
        url: `hiiko_environment/${envId}/task_category`,
      }),
      providesTags: ['taskCategory'],
    }),

    // Garage - Transport
    getVehicleAccountingMethods: builder.query<IAccountingMethod[], void>({
      query: () => ({
        url: `garage/accounting-method/vehicle`,
      }),
    }),

    getTransportById: builder.query<
      ITransport,
      { envId: number; transportId: number; companyId: number }
    >({
      query: ({ envId, transportId, companyId }) => ({
        url: `hiiko_environment/${envId}/company/${companyId}/garage/vehicle/${transportId}`,
      }),
      providesTags: ['transport'],
    }),

    getTransport: builder.query<
      ITransportResponse,
      {
        envId: number;
        companyId: number | null;
        companyGroupId: number | null;

        pageSize: number;
        currentPage: number;
        filters: ITransportFilters;
        sort: ITableSort;
      }
    >({
      query: ({
        companyId,
        companyGroupId,
        pageSize,
        currentPage,
        filters,
        sort,
        envId,
      }) => ({
        url: `hiiko_environment/${envId}/garage/vehicle`,
        params: {
          size: pageSize,
          page: currentPage,
          task_category_ids: filters.taskCategoryIds?.length
            ? filters.taskCategoryIds
            : undefined,
          search: filters.search?.trim() || undefined,
          sort: geTableSortQueryParam(sort),
          company_id: companyId ?? undefined,
          company_group_id: companyGroupId ?? undefined,
        },
      }),
      providesTags: ['transport'],
    }),

    getAllTransportTypes: builder.query<IGarageUnitType[], void>({
      query: () => ({
        url: `garage/type/vehicle`,
      }),
    }),

    getAllGpsTransport: builder.query<
      IGpsTransport[],
      { envId: number; companyId?: number }
    >({
      query: ({ companyId, envId }) => ({
        url: `hiiko_environment/${envId}/garage/gps/vehicle`,
        params: { company_id: companyId },
      }),
      providesTags: ['transport'],
    }),

    addTransport: builder.mutation<unknown, IAddTransportPayload>({
      query: ({ companyId, envId, ...data }) => ({
        method: 'POST',
        url: `hiiko_environment/${envId}/company/${companyId}/garage/vehicle`,
        body: data,
      }),
      invalidatesTags: ['transport'],
    }),

    updateTransport: builder.mutation<unknown, IUpdateTransportPayload>({
      query: ({ companyId, envId, transportId, ...data }) => ({
        method: 'PUT',
        url: `hiiko_environment/${envId}/company/${companyId}/garage/vehicle/${transportId}`,
        body: data,
      }),
      invalidatesTags: ['transport'],
    }),

    archiveTransport: builder.mutation<
      unknown,
      { companyId: number; envId: number; transportId: number }
    >({
      query: ({ companyId, envId, transportId }) => ({
        method: 'PATCH',
        url: `hiiko_environment/${envId}/company/${companyId}/garage/vehicle/${transportId}/archive`,
      }),
      invalidatesTags: ['transport'],
    }),

    // Garage - Implement
    getImplementAccountingMethods: builder.query<IAccountingMethod[], void>({
      query: () => ({
        url: `garage/accounting-method/implement`,
      }),
    }),

    getImplement: builder.query<
      IImplementResponse,
      {
        envId: number;
        companyId: number | null;
        companyGroupId: number | null;
        pageSize: number;
        currentPage: number;
        filters: ITransportFilters;
        sort: ITableSort;
      }
    >({
      query: ({
        companyId,
        companyGroupId,
        pageSize,
        currentPage,
        filters,
        sort,
        envId,
      }) => ({
        url: `hiiko_environment/${envId}/garage/implement`,
        params: {
          size: pageSize,
          page: currentPage,
          task_category_ids: filters.taskCategoryIds?.length
            ? filters.taskCategoryIds
            : undefined,
          search: filters.search?.trim() || undefined,
          sort: geTableSortQueryParam(sort),
          company_id: companyId || undefined,
          company_group_id: companyGroupId || undefined,
        },
      }),
      providesTags: ['implement'],
    }),

    addImplement: builder.mutation<unknown, IAddImplementPayload>({
      query: ({ companyId, envId, ...data }) => ({
        method: 'POST',
        url: `hiiko_environment/${envId}/company/${companyId}/garage/implement`,
        body: data,
      }),
      invalidatesTags: ['implement'],
    }),

    updateImplement: builder.mutation<unknown, IUpdateImplementPayload>({
      query: ({ companyId, envId, implementId, ...data }) => ({
        method: 'PUT',
        url: `hiiko_environment/${envId}/company/${companyId}/garage/implement/${implementId}`,
        body: data,
      }),
      invalidatesTags: ['implement'],
    }),

    getAllImplementTypes: builder.query<IGarageUnitType[], void>({
      query: () => ({
        url: `garage/type/implement`,
      }),
    }),

    getAllGpsImplements: builder.query<
      IGpsImplement[],
      { companyId?: number; envId: number }
    >({
      query: ({ companyId, envId }) => ({
        url: `hiiko_environment/${envId}/garage/gps/implement`,
        params: { company_id: companyId },
      }),
    }),

    getImplementById: builder.query<
      IImplement,
      { envId: number; implementId: number; companyId: number }
    >({
      query: ({ envId, implementId, companyId }) => ({
        url: `hiiko_environment/${envId}/company/${companyId}/garage/implement/${implementId}`,
      }),
      providesTags: ['implement'],
    }),

    archiveImplement: builder.mutation<
      unknown,
      { companyId: number; envId: number; implementId: number }
    >({
      query: ({ companyId, envId, implementId }) => ({
        method: 'PATCH',
        url: `hiiko_environment/${envId}/company/${companyId}/garage/implement/${implementId}/archive`,
      }),
      invalidatesTags: ['implement'],
    }),

    // Garage - Employee
    getAllGpsEmployee: builder.query<
      IGpsEmployee[],
      { envId: number; companyId?: number }
    >({
      query: ({ companyId, envId }) => ({
        url: `hiiko_environment/${envId}/garage/gps/employee`,
        params: { company_id: companyId },
      }),
      providesTags: ['employee'],
    }),

    getEmployeeAccountingMethods: builder.query<IAccountingMethod[], void>({
      query: () => ({
        url: `garage/accounting-method/employee`,
      }),
    }),

    getEmployee: builder.query<
      IEmployeeResponse,
      {
        envId: number;
        companyId: number | null;
        companyGroupId: number | null;
        pageSize: number;
        currentPage: number;
        filters: IEmployeeFilters;
        sort: ITableSort;
      }
    >({
      query: ({
        companyId,
        companyGroupId,
        pageSize,
        currentPage,
        sort,
        envId,
        filters,
      }) => ({
        url: `hiiko_environment/${envId}/garage/employee`,
        params: {
          size: pageSize,
          page: currentPage,
          search: filters.search?.trim() || undefined,
          birthYear: filters.birthYear,
          sort: geTableSortQueryParam(sort),
          company_id: companyId || undefined,
          company_group_id: companyGroupId || undefined,
        },
      }),
      providesTags: ['employee'],
    }),

    addEmployee: builder.mutation<unknown, IAddEmployeePayload>({
      query: ({ companyId, envId, ...data }) => ({
        method: 'POST',
        url: `hiiko_environment/${envId}/company/${companyId}/garage/employee`,
        body: data,
      }),
      invalidatesTags: ['employee'],
    }),

    updateEmployee: builder.mutation<unknown, IUpdateEmployeePayload>({
      query: ({ companyId, envId, employeeId, ...data }) => ({
        method: 'PUT',
        url: `hiiko_environment/${envId}/company/${companyId}/garage/employee/${employeeId}`,
        body: data,
      }),
      invalidatesTags: ['employee'],
    }),

    getEmployeeById: builder.query<
      IEmployee,
      { envId: number; employeeId: number; companyId: number }
    >({
      query: ({ envId, employeeId, companyId }) => ({
        url: `hiiko_environment/${envId}/company/${companyId}/garage/employee/${employeeId}`,
      }),
      providesTags: ['employee'],
    }),

    archiveEmployee: builder.mutation<
      unknown,
      { companyId: number; envId: number; employeeId: number }
    >({
      query: ({ companyId, envId, employeeId }) => ({
        method: 'PATCH',
        url: `hiiko_environment/${envId}/company/${companyId}/garage/employee/${employeeId}/archive`,
      }),
      invalidatesTags: ['employee'],
    }),

    // Task
    createOrUpdateTaskNotes: builder.mutation<
      unknown,
      { envId: number; taskId: number; notes: string }
    >({
      query: ({ envId, taskId, notes }) => ({
        url: `hiiko_environment/${envId}/task/${taskId}/note`,
        method: 'POST',
        body: { note: notes },
        responseHandler: (response: { text: () => any }) => response.text(),
      }),
      invalidatesTags: ['task'],
    }),

    addTaskPlannedResource: builder.mutation<
      ITask,
      { envId: number; taskId: number; norm: number; resourceId: number }
    >({
      query: ({ envId, taskId, norm, resourceId }) => ({
        url: `hiiko_environment/${envId}/task/${taskId}/planned-resources`,
        method: 'POST',
        body: { norm, resourceId },
      }),
      invalidatesTags: ['task'],
    }),

    createTask: builder.mutation<
      ITask,
      { envId: number; taskData: ICreateTask }
    >({
      query: ({ envId, taskData }) => ({
        url: `hiiko_environment/${envId}/task`,
        method: 'POST',
        body: taskData,
      }),
      invalidatesTags: ['task'],
    }),

    getTaskList: builder.query<
      ITaskListResponse,
      {
        envId: number;
        taskCategoryIds?: number[];
        startDate?: string | null;
        endDate?: string | null;
        companyGroupId: number | null;
        companyId: number | null;
      }
    >({
      query: ({
        envId,
        taskCategoryIds,
        companyGroupId,
        startDate,
        endDate,
        companyId,
      }) => ({
        url: `hiiko_environment/${envId}/task`,
        params: {
          company_group_id: companyGroupId ?? undefined,
          company_id: companyId ?? undefined,
          sort: 'createdAt,desc',
          category_ids: taskCategoryIds,
          start_date: startDate || undefined,
          end_date: endDate || undefined,
        },
      }),
      providesTags: ['task'],
    }),

    getTask: builder.query<ITask, { taskId: number; envId: number }>({
      query: ({ taskId, envId }) => ({
        url: `hiiko_environment/${envId}/task/${taskId}`,
      }),
      providesTags: ['task'],
    }),

    deleteTask: builder.mutation<unknown, { taskId: number; envId: number }>({
      query: ({ taskId, envId }) => ({
        method: 'DELETE',
        url: `hiiko_environment/${envId}/task/${taskId}`,
      }),
      invalidatesTags: ['task'],
    }),

    // Task routes

    assignRouteToTask: builder.mutation<
      unknown,
      { taskId: number; envId: number; routeId: number }
    >({
      query: ({ taskId, envId, routeId }) => ({
        method: 'PUT',
        url: `hiiko_environment/${envId}/gps_route/${routeId}/${taskId}`,
      }),
      invalidatesTags: ['task'],
    }),

    getTaskRoutes: builder.query<
      RouteData[],
      { taskId: number; envId: number }
    >({
      query: ({ taskId, envId }) => ({
        method: 'GET',
        url: `hiiko_environment/${envId}/task/${taskId}/routes`,
      }),

      transformResponse: (routesResponse: RouteData[]): RouteData[] => {
        return routesResponse.map(route => ({
          ...route,
          geometry: {
            ...route.geometry,
            coordinates: reorder([[route.geometry.coordinates as any]]),
          },
        }));
      },

      providesTags: ['task'],
    }),

    // Task recommendation

    createTaskRecommendation: builder.mutation<
      ITask,
      { envId: number; data: ICreateTaskRecommendationBody }
    >({
      query: ({ envId, data }) => ({
        method: 'POST',
        url: `hiiko_environment/${envId}/task/recommendation`,
        body: data,
      }),
      invalidatesTags: ['task'],
    }),

    updateTaskRecommendation: builder.mutation<
      ITask,
      { envId: number; data: IEditTaskRecommendationBody; taskId: number }
    >({
      query: ({ envId, data, taskId }) => ({
        method: 'PUT',
        url: `hiiko_environment/${envId}/task/${taskId}/recommendation`,
        body: data,
      }),
      invalidatesTags: ['task'],
    }),

    acceptTaskRecommendation: builder.mutation<
      unknown,
      { envId: number; taskId: number }
    >({
      query: ({ envId, taskId }) => ({
        method: 'PATCH',
        url: `hiiko_environment/${envId}/task/${taskId}/recommendation/accept`,
      }),
      invalidatesTags: ['task'],
    }),

    declineTaskRecommendation: builder.mutation<
      unknown,
      { envId: number; taskId: number; comment: string }
    >({
      query: ({ envId, taskId, comment }) => ({
        method: 'PATCH',
        url: `hiiko_environment/${envId}/task/${taskId}/recommendation/decline`,
        body: { comment },
      }),
      invalidatesTags: ['task'],
    }),

    // Recipe
    getRecipe: builder.query<
      IRecipeResponse,
      {
        envId: number;
        pageSize: number;
        currentPage: number;
        filters: IRecipeFilters;
        sort: ITableSort;
      }
    >({
      query: ({
        pageSize,
        currentPage,
        filters: { name, cultureIds, categoryIds, bbchFrom, bbchTo, userId },
        sort,
        envId,
      }) => ({
        url: `hiiko_environment/${envId}/recipe`,
        params: {
          size: pageSize,
          page: currentPage,
          sort: geTableSortQueryParam(sort),

          name: name?.trim() || undefined,
          cultureIds: cultureIds?.length ? cultureIds : undefined,
          categoryIds: categoryIds?.length ? categoryIds : undefined,
          bbchFrom: bbchFrom || undefined,
          bbchTo: bbchTo || undefined,
          userId: userId || undefined,
        },
      }),
      providesTags: ['recipe'],
    }),

    getRecipeById: builder.query<
      IRecipe,
      {
        envId: number;
        recipeId: number;
      }
    >({
      query: ({ recipeId, envId }) => ({
        url: `hiiko_environment/${envId}/recipe/${recipeId}`,
      }),
      providesTags: ['recipe'],
    }),

    getRecipeCategories: builder.query<IRecipeCategory[], void>({
      query: () => ({
        url: `recipe/category`,
      }),
    }),

    createRecipe: builder.mutation<IRecipe, ICreateRecipePayload>({
      query: ({ envId, ...data }) => ({
        method: 'POST',
        url: `hiiko_environment/${envId}/recipe`,
        body: data,
      }),
      invalidatesTags: ['recipe'],
    }),

    updateRecipe: builder.mutation<IRecipe, IUpdateRecipePayload>({
      query: ({ envId, recipeId, ...data }) => ({
        method: 'PUT',
        url: `hiiko_environment/${envId}/recipe/${recipeId}`,
        body: data,
      }),
      invalidatesTags: ['recipe'],
    }),

    archiveRecipe: builder.mutation<
      unknown,
      { envId: number; recipeId: number }
    >({
      query: ({ envId, recipeId }) => ({
        method: 'PATCH',
        url: `/hiiko_environment/${envId}/recipe/${recipeId}`,
      }),
      invalidatesTags: ['recipe'],
    }),

    // Culture
    getAllCulture: builder.query<ICulture[], void>({
      transformResponse: (response: ICulture[]) =>
        response?.sort((a, b) => a.name.localeCompare(b.name)),
      query: () => ({
        url: `cultures`,
      }),
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  // Polygons
  useUploadPolygonMutation,
  useUpdatePolygonGeometryMutation,

  // Transport on map
  useGetTransportPositionQuery,
  useRemoveRouteMutation,

  // Fields on map
  useGetFieldsGeometryQuery,

  // Routes on map
  useGetRoutesQuery,
  useAssignBeaconMutation,
  useAssignSlimMutation,

  // Company
  useAcceptSharedCompanyMutation,
  useRemoveSharedCompanyMutation,

  // Company Group
  useCreateCompanyGroupMutation,
  useGetAllCompanyGroupsQuery,
  useUpdateCompanyGroupMutation,
  useArchiveCompanyGroupMutation,

  // Permissions
  useGetPermissionsQuery,

  // User
  useGetAllUsersQuery,
  useGetPrivacyPolicyQuery,
  useAcceptPrivacyPolicyMutation,

  useGetSeasonsForFieldImportQuery,
  useGetUsedResourcesByResourceTypeQuery,

  // Resource
  useGetAllResourceTypesQuery,
  useGetAllResourcesQuery,

  // Used Resource
  useGetUsedResourcePricesQuery,
  useAddUsedResourcePricesMutation,
  useGetTaskCategoryPricesQuery,
  useUpdateTaskCategoryPriceMutation,

  // Garage - Transport
  useGetVehicleAccountingMethodsQuery,
  useGetTaskCategoriesQuery,
  useGetTransportQuery,
  useGetAllTransportTypesQuery,
  useGetAllGpsTransportQuery,
  useAddTransportMutation,
  useUpdateTransportMutation,
  useGetTransportByIdQuery,
  useArchiveTransportMutation,

  // Garage - Implement
  useGetImplementAccountingMethodsQuery,
  useGetImplementQuery,
  useAddImplementMutation,
  useUpdateImplementMutation,
  useGetAllImplementTypesQuery,
  useGetAllGpsImplementsQuery,
  useGetImplementByIdQuery,
  useArchiveImplementMutation,

  // Garage - Employee
  useGetAllGpsEmployeeQuery,
  useGetEmployeeQuery,
  useGetEmployeeAccountingMethodsQuery,
  useAddEmployeeMutation,
  useUpdateEmployeeMutation,
  useGetEmployeeByIdQuery,
  useArchiveEmployeeMutation,

  // Task
  useCreateTaskMutation,
  useAddTaskPlannedResourceMutation,
  useCreateOrUpdateTaskNotesMutation,
  useGetTaskListQuery,
  useGetTaskQuery,
  useDeleteTaskMutation,

  // Task routes
  useAssignRouteToTaskMutation,
  useGetTaskRoutesQuery,

  // Task recommendation
  useCreateTaskRecommendationMutation,
  useUpdateTaskRecommendationMutation,
  useAcceptTaskRecommendationMutation,
  useDeclineTaskRecommendationMutation,

  // Recipe
  useGetRecipeQuery,
  useGetRecipeByIdQuery,
  useGetRecipeCategoriesQuery,
  useCreateRecipeMutation,
  useUpdateRecipeMutation,
  useArchiveRecipeMutation,

  // Culture
  useGetAllCultureQuery,
} = api;
